import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const SignupDialog = lazy(() => import("@/components/SignupDialog"));

const HeroSection = () => {
  const [signupOpen, setSignupOpen] = useState(false);
  const { t } = useLanguage();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#teken") {
      setSignupOpen(true);
    }
  }, [hash]);

  return (
    <>
      <section className="relative min-h-[85vh] overflow-hidden">
        {/* Background image with parallax-like depth */}
        <div className="absolute inset-0">
          <picture>
            <source
              media="(max-width: 768px)"
              srcSet="/images/grachten-amsterdam-mobile.webp"
              type="image/webp"
            />
            <img
              src="/images/grachten-amsterdam.webp"
              alt="Amsterdamse gracht met brug en water"
              className="h-full w-full scale-105 object-cover"
              width={1920}
              height={1280}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40 md:to-background/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-background/20" />
        </div>

        <div className="container relative flex min-h-[85vh] items-center py-20">
          <div className="max-w-2xl">
            <p
              className="animate-fade-in-up font-body text-sm font-bold uppercase tracking-[0.2em] text-primary md:text-base"
            >
              {t("19 maart · Uitslag binnen", "March 19 · Results are in")}
            </p>

            <h1 className="mt-4 text-[2.75rem] font-black leading-[1.1] tracking-tight opacity-0 animate-fade-in md:text-6xl lg:text-7xl" style={{ animationDelay: "200ms" }}>
              {t("Amsterdam heeft", "Amsterdam has")}{" "}
              <span className="relative inline-block text-primary">
                {t("gekozen.", "voted.")}
                <span className="absolute -bottom-1 left-0 h-1 w-full origin-left animate-underline-grow bg-primary" />
              </span>
            </h1>

            <p
              className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground opacity-0 animate-fade-in md:text-lg"
              style={{ animationDelay: "400ms" }}
            >
              {t(
                "GroenLinks is de grootste partij met 10 zetels, gevolgd door D66 (8) en PvdA (7). De opkomst was 46,9%. Bekijk de uitslag en onze coalitieanalyse.",
                "GroenLinks is the largest party with 10 seats, followed by D66 (8) and PvdA (7). Turnout was 46.9%. View the results and our coalition analysis."
              )}
            </p>

            <p
              className="mt-4 font-body text-sm font-bold uppercase tracking-widest text-foreground opacity-0 animate-fade-in md:text-base"
              style={{ animationDelay: "500ms" }}
            >
              {t("GroenLinks wint, PvdA verliest.", "GroenLinks wins, PvdA loses.")}
            </p>

            <div
              className="mt-10 flex flex-wrap gap-4 opacity-0 animate-fade-in"
              style={{ animationDelay: "600ms" }}
            >
              <Button size="lg" asChild>
                <Link to="/verkiezingen">{t("Bekijk de uitslag", "View the results")}</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/feiten">{t("Bekijk de cijfers", "View the facts")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <SignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
      </Suspense>
    </>
  );
};

export default HeroSection;
